import { createApiRef } from '@backstage/core-plugin-api';

// Export shared components
export { ServiceUnavailableError } from './components/ServiceUnavailableError';
export { ErrorBoundary } from './components/ErrorBoundary';
export { LoadingSpinner, type LoadingSpinnerProps } from './components/LoadingSpinner';
export { ChartContainer, type ChartContainerProps } from './components/charts/ChartContainer';
export { ChartTooltip, type ChartTooltipProps } from './components/charts/ChartTooltip';
export { NavigationLink, type NavigationLinkProps } from './components/NavigationLink';

// Export shared utilities
export { 
  scrollbarStyles, 
  colors, 
  getBadgeColor, 
  shadows,
  commonInlineStyles
} from './utils/commonStyles';
export { safeLocalStorage } from './utils/safeLocalStorage';
// Export error handling utilities
export {
  DfpErrorType,
  type DfpErrorResponse,
  createErrorResponse,
  parseErrorResponse,
  extractErrorMessage,
  isNetworkError,
  isTimeoutError,
  createErrorFromStatus,
  isServiceUnavailableError,
} from './utils/errorHandling';

// Export formatting utilities
export {
  formatTrendValue,
  formatEffortHours,
  formatDateForAPI,
  formatNumber,
  formatPercentage,
  getDateRange,
  formatDateHumanReadable,
} from './utils/formatting';

// Export date formatting utilities
export {
  formatDateForChart,
} from './utils/dateFormatting';

// Export Technical Debt color constants and utilities
export {
  SEVERITY_COLORS,
  SOLUTION_STATUS_COLORS,
  DEBT_STATUS_COLORS,
  PRIORITY_COLORS,
  CATEGORY_COLORS,
  SOURCE_COLORS,
  CHART_COLORS,
  COLOR_PALETTE,
  getColor,
  getColorWithFallback,
  getSeverityColor,
  getSolutionStatusColor,
  getPriorityColor,
  getCategoryColor,
  getSourceColor,
} from './constants/technicalDebtColors';

// Export Technical Debt constants
export {
  SEVERITY_LEVELS,
  DEBT_STATUS,
  TECHNICAL_DEBT_INFRASTRUCTURE_CATEGORY,
  TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_LABELS,
  TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_ORDERED_KEYS,
  TECHNICAL_DEBT_INFRASTRUCTURE_METRIC_SUPPLEMENTAL_KEYS,
  SOLUTION_STATUS,
  TIME_PERIODS,
  PRIORITY_LEVELS,
  SEVERITY_COLORS_THEME,
  STATUS_COLORS_THEME,
  SEVERITY_ICONS,
  STATUS_ICONS,
  LABELS,
  getSeverityColorTheme,
  getStatusColorTheme,
  getSeverityChipColor,
  getPriorityBadgeStyle,
} from './constants/technicalDebt';

// Export validation utilities
export {
  VALIDATION_PATTERNS,
  validateProjectKey,
  validateDebtItemId,
  validateProjectName,
  validateInteger,
  validateDecimal,
} from './utils/validation';

export {
  parseTechnicalDebtItemMetrics,
  humanizeTechnicalDebtMetricKey,
  formatTechnicalDebtStandardizedTagsDisplay,
  formatTechnicalDebtTagsDisplay,
  formatTechnicalDebtInfraScalarValue,
} from './utils/technicalDebtMetrics';

// Export chart configuration
export {
  CHART_DEFAULTS,
  CHART_CONTAINER_STYLES,
  CHART_TOOLTIP_CONFIG,
  getChartContainerBackground,
  getTooltipContentStyle,
  getTooltipItemStyle,
  getTooltipLabelStyle,
} from './constants/chartConfig';

// Export number input hook
export {
  useNumberInput,
  type UseNumberInputOptions,
} from './hooks/useNumberInput';

// Export chart type definitions
export type {
  ChartDataPoint,
  DistributionKeys,
  PieChartDataPoint,
  ChartMetric,
} from './types/chartTypes';

// Export shared hooks
export { useFavoriteProducts } from './hooks/useFavoriteProducts';
export { useTrendStyles } from './hooks/useTrendStyles';
export { useMarkdownStyles } from './hooks/useMarkdownStyles';

// Export constants
export {
  DFP_ROUTES,
  DFP_LEGACY_ROUTES,
  DFP_LEGACY_REDIRECTS,
  DFP_NAVIGATION,
  DFP_OVERVIEW_ROUTES,
  applyRouteParams,
  dfpRelativePath,
  buildRouteWithParams,
  extractProductMasterCode,
  extractStandardCode,
  extractMintProductCode,
  healthHubTechnicalStackRepo,
} from './constants/routes';
export type { DfpLegacyRedirect, DfpNavIconKey, DfpNavItem } from './constants/routes';

// --- Types for Overview Page ---
export interface ProjectInfo {
  service_name: string;
  product_owner: string;
  mission: string;
}

export interface OverviewHeader {
  name: string;
  project_info: ProjectInfo;
}

export interface CoreFeature {
  key: string;
  title: string;
  description: string;
  features: string[];
}

export interface VisualizationItem {
  key: string;
  title: string;
  type: string;
  description: string;
  source: string;
  call_to_action?: {
    text: string;
  };
}

export interface OverviewSection {
  id: string;
  type?: string;
  title: string;
  description: string;
  greeting?: string;
  core_features?: CoreFeature[];
  items?: VisualizationItem[];
}

// --- Types for Product Details ---
export interface ProductDetails {
  metadata: {
    product_code: string;
    product_id: string;
    description: string;
    name_en: string;
    name_jp: string;
    segment: string;
    domain: string;
    division: string;
    category?: string;
    lifecycle?: string;
    department?: string;
    group?: string;
    company?: string;
  };
  spec: {
    tags: string[];
    services: string[];
    relations: { [key: string]: any } | Array<{
      type: string;
      target_ref: string;
    }>;
    contacts: { [key: string]: any } | {
      channel: string;
      owner: string;
      users: any[];
    };
    tech_stack: {
      backend: { [key: string]: string };
      frontend: { [key: string]: string };
      github_url?: string;
    };
    runbooks: any[];
    product_code_mapping?: {
      k8s_mapping: string;
      backstage_mapping: string;
      tech_stack_mapping: string;
    };
    end_of_life: string[] | { [key: string]: any } | string;
    is_active?: boolean;
  };
  maturity_level: {
    mint: {
      categories: {
        [category: string]: {
          total_rules: number;
          passed_rules: number;
          failed_rules: number;
          skipped_rules: number;
          pass_percentage: number;
        };
      };
    };
  };
  updated_at: string;
}

// --- Types for Maturity Level ---
export interface MaturityScoreboard {
  Reliability: number;
  Datadog: number;
  Security: number;
  Deprecations: number;
  Others: number;
}

export interface MaturityProduct {
  product_code: string;
  company: string;
  group: string;
  department: string;
  results: {
    Reliability: 'pass' | 'fail' | 'skip';
    Datadog: 'pass' | 'fail' | 'skip';
    Security: 'pass' | 'fail' | 'skip';
    Deprecations: 'pass' | 'fail' | 'skip';
    Others: 'pass' | 'fail' | 'skip';
  };
}

export interface MaturityLevelResponse {
  scoreboard: MaturityScoreboard;
  products: MaturityProduct[];
}

// --- Types for Mint API ---
export interface MintCategoryData {
  total_rules: number;
  passed_rules: number;
  failed_rules: number;
  skipped_rules: number;
  pass_percentage: number;
}

export interface MintProductData {
  segment?: string;
  domain?: string;
  division?: string;
  department?: string;
  group?: string;
  company?: string;
  categories: {
    [category: string]: MintCategoryData;
  };
}

export interface MintSummaryResponse {
  overall_summary: {
    categories: {
      [category: string]: MintCategoryData;
    };
    total_products: number;
  };
  products: {
    [productCode: string]: MintProductData;
  };
}

export interface MintRuleResult {
  rule_id: string;
  result: 'pass' | 'fail' | 'skip';
  description: string;
  link: string;
  comments: Array<{
    text: string;
    failed: boolean;
  }>;
}

export interface MintProductResults {
  product_code: string;
  summary: {
    categories: {
      [category: string]: MintCategoryData;
    };
  };
  results: {
    [category: string]: {
      results: MintRuleResult[];
    };
  };
  updated_at: string;
}

export interface MaturityInspectionRule {
  result: 'pass' | 'fail' | 'skip';
  details: string;
}

export interface MaturityProductDetails {
  [ruleName: string]: MaturityInspectionRule;
}

// --- Types for Product List API (New Format) ---
export interface ProductListDivision {
  division_id: string;
  product_codes: string[];
}

export interface ProductListDomain {
  domain_id: string;
  divisions: { [divisionName: string]: ProductListDivision };
}

export interface ProductListSegment {
  segment_id: string;
  domains: { [domainName: string]: ProductListDomain };
}

export type ProductListResponse = { [segmentName: string]: ProductListSegment };

interface LegacyProductListResponse {
  products: Array<{ product_code?: string }>;
}

export interface FlatListedProduct {
  product_code: string;
  segmentName: string;
  domainName: string;
  divisionName: string;
}

/**
 * Screens still store org as company, group, and department.
 * Product Master APIs send segment, domain, and division. Prefer those.
 */
export function productOrgNames(row: {
  segment?: string | null;
  domain?: string | null;
  division?: string | null;
  company?: string | null;
  group?: string | null;
  department?: string | null;
} | null | undefined): { company: string; group: string; department: string } {
  const text = (value: string | null | undefined) => value?.trim() ?? '';
  return {
    company: text(row?.segment) || text(row?.company),
    group: text(row?.domain) || text(row?.group),
    department: text(row?.division) || text(row?.department),
  };
}

export function flattenProductList(
  productList: ProductListResponse | null | undefined,
): FlatListedProduct[] {
  if (!productList || Array.isArray(productList)) {
    return [];
  }
  const products: FlatListedProduct[] = [];
  for (const [segmentName, segment] of Object.entries(productList)) {
    for (const [domainName, domain] of Object.entries(segment?.domains || {})) {
      for (const [divisionName, division] of Object.entries(domain?.divisions || {})) {
        for (const productCode of division?.product_codes || []) {
          products.push({
            product_code: productCode,
            segmentName,
            domainName,
            divisionName,
          });
        }
      }
    }
  }
  return products;
}

function isLegacyProductList(
  productList: ProductListResponse | LegacyProductListResponse | null | undefined,
): productList is LegacyProductListResponse {
  return Boolean(
    productList && 'products' in productList && Array.isArray(productList.products),
  );
}

export function productCodesFromProductList(
  productList: ProductListResponse | LegacyProductListResponse | null | undefined,
): string[] {
  if (isLegacyProductList(productList)) {
    return productList.products.flatMap(p =>
      p.product_code ? [p.product_code] : [],
    );
  }
  return flattenProductList(productList).map(p => p.product_code);
}

// --- Types for Standard Compliance ---
export type StandardizationLevel = 'STANDARD' | 'BEST_PRACTICE' | 'STANDARD_FOR_NEW_PRODUCTS';

export interface StandardAdoptionStatus {
  ADOPTED: number;
  IN_PROGRESS: number;
  NOT_STARTED: number;
  OUT_OF_SCOPE: number;
}

export interface StandardDetails {
  code: string;
  name: string;
  standardization_level: StandardizationLevel;
  standardization_area?: string;
  current_version: string;
  description: string;
  status: string;
  adoption_summary: {
    ADOPTED: {
      count: number;
      products: string[];
    };
    IN_PROGRESS: {
      count: number;
      products: string[];
    };
    NOT_STARTED: {
      count: number;
      products: string[];
    };
    OUT_OF_SCOPE: {
      count: number;
      products: string[];
    };
  };
  valid_from: string;
  valid_to: string | null;
  last_updated: string;
}

export interface LogValidationRule {
  status: 'success' | 'failed';
  description: string;
  missing_fields?: string[];
}

export interface LogValidationService {
  overall_status: 'success' | 'failed';
  rule_1?: LogValidationRule;
  rule_2?: LogValidationRule;
  rule_3?: LogValidationRule;
  rule_4?: LogValidationRule;
  [ruleName: string]: any; // Allow additional rules dynamically
}

export interface LogValidationResult {
  results: {
    [serviceName: string]: LogValidationService;
  };
  last_scanning_time: string;
}

export interface ProductStandardDetails {
  product_code: string;
  department: string;
  group: string;
  company: string;
  standard_adoption_status: Record<string, string | {
    status: string;
    validation?: LogValidationResult;
  }>;
  last_updated: string;
  log_std_validation?: LogValidationResult;
}

export interface StandardComplianceResponse {
  overall_summary: {
    standards: {
      [standardCode: string]: {
        name: string;
        standardization_level: StandardizationLevel;
        standardization_area?: string;
        adoption_status: StandardAdoptionStatus;
      };
    };
    products: {
      [productCode: string]: {
        segment?: string;
        domain?: string;
        division?: string;
        department: string;
        group: string;
        company: string;
        adoption_status: StandardAdoptionStatus;
        log_std_validation_status: 'passed' | 'failed' | null;
      };
    };
  };
}

// Separate API response types for standards and products endpoints
export interface StandardsAdoptionResponse {
  standards: {
    [standardCode: string]: {
      name: string;
      standardization_level: StandardizationLevel;
      standardization_area?: string;
      adoption_status: StandardAdoptionStatus;
    };
  };
}

export interface ProductsComplianceResponse {
  products: {
    [productCode: string]: {
      segment?: string;
      domain?: string;
      division?: string;
      department: string;
      group: string;
      company: string;
      adoption_status: StandardAdoptionStatus;
      log_std_validation_status: 'passed' | 'failed' | null;
    };
  };
}

// Standard Compliance Summary (trend) API
export interface StandardComplianceMetricTrendDto {
  current: number;
  previous: number;
  change: number;
  change_percent: number;
  trend: 'increasing' | 'decreasing' | 'stable';
}

export interface StandardAdoptionTrendsDto {
  adopted: StandardComplianceMetricTrendDto;
  in_progress: StandardComplianceMetricTrendDto;
  not_started: StandardComplianceMetricTrendDto;
  out_of_scope: StandardComplianceMetricTrendDto;
}

export interface StandardComplianceTrendsDto {
  total_standards?: StandardComplianceMetricTrendDto;
  total_products?: StandardComplianceMetricTrendDto;
  standards?: Record<string, StandardAdoptionTrendsDto>;
}

export interface StandardComplianceSummaryDto {
  id: string;
  total_standards: number;
  total_products: number;
  snapshot_date: string;
  created_at: string;
  summary_data?: Record<string, { adopted?: number; in_progress?: number; not_started?: number; out_of_scope?: number }>;
}

export interface StandardComplianceSummaryListResponse {
  summaries: StandardComplianceSummaryDto[];
  count: number;
  date_range: { start_date: string; end_date: string };
  trends?: StandardComplianceTrendsDto | null;
}

// --- Types for Common Platform Integration ---
export interface PlatformIntegrationStatus {
  COVERED_BY_PLATFORM: number;
  COVERED_BY_OWN: number;
  INTEGRATING_PLATFORM: number;
  NOT_COVERED: number;
  NO_NEED_TO_COVER: number;
}

export interface PlatformCategory {
  category_name: string;
  category_code: string;
  platforms: Array<{
    code: string;
    platform_integration_status: PlatformIntegrationStatus;
  }>;
}

export interface CommonPlatformOverallSummary {
  overall_summary: {
    platforms: {
      [categoryCode: string]: {
        category_name: string;
        platforms: Array<{
          code: string;
          platform_integration_status: PlatformIntegrationStatus;
        }>;
      };
    };
    products: {
      [productCode: string]: {
        segment?: string;
        domain?: string;
        division?: string;
        department: string;
        group: string;
        company: string;
        platform_integration_status: {
          [platformCode: string]: string;
        };
      };
    };
  };
}

export interface PlatformIntegrationSummary {
  code: string;
  category_name: string;
  category_code: string;
  current_version: string;
  description: string;
  status: string;
  integration_summary: {
    COVERED_BY_PLATFORM: {
      count: number;
      products: string[];
    };
    COVERED_BY_OWN: {
      count: number;
      products: string[];
    };
    INTEGRATING_PLATFORM: {
      count: number;
      products: string[];
    };
    NOT_COVERED: {
      count: number;
      products: string[];
    };
    NO_NEED_TO_COVER: {
      count: number;
      products: string[];
    };
  };
  valid_from: string;
  valid_to: string | null;
  last_updated: string;
}

export interface ProductPlatformIntegration {
  product_code: string;
  platform_integration_status: {
    [categoryCode: string]: {
      category_name: string;
      platforms: Array<{
        code: string;
        status: string;
      }>;
    };
  };
  last_updated: string;
}

// --- Types for Technical Debt ---
export interface TechnicalDebtSeverity {
  CRITICAL: number;
  HIGH: number;
  MEDIUM: number;
  LOW: number;
}

export interface TechnicalDebtByCategory {
  [category: string]: number;
}

export interface TechnicalDebtBySource {
  [source: string]: number;
}

export interface TechnicalDebtProject {
  project_key: string;
  project_name: string;
  product_code: string;
  segment?: string;
  domain?: string;
  division?: string;
  company: string;
  group: string;
  department: string;
  total_debt_items: number;
  avg_age: number;
  severity: TechnicalDebtSeverity;
}

export interface TechnicalDebtOverallSummary {
  total_debt_items: number;
  total_projects: number;
  average_risk_score: number;
  average_age: number;
  oldest_debt: number;
  newest_debt: number;
  collection_time_ms: number;
  average_debt_ratio: number;
  projects_meeting_coverage_gate: number;
  projects_with_critical_issues: number;
  avg_cognitive_complexity: number;
  projects_exceeding_duplication_limit: number;
  overall_availability: number;
  avg_error_rate: number;
  avg_p95_latency: number;
  avg_success_rate: number;
  total_sev1_incidents: number;
  total_sev2_incidents: number;
  by_category: TechnicalDebtByCategory;
  by_severity: TechnicalDebtSeverity;
  by_source: TechnicalDebtBySource;
  report_timestamp: string;
}

export interface TechnicalDebtDashboardResponse {
  overall_summary: TechnicalDebtOverallSummary;
  projects: TechnicalDebtProject[];
}

export interface TechnicalDebtCategory {
  category_code: string;
  category_name: string;
  total_debt_items: number;
  severity_breakdown: TechnicalDebtSeverity;
  estimated_effort_hours: number;
}

export interface TechnicalDebtItem {
  debt_code: string;
  title: string;
  description: string;
  category: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'DEFERRED';
  affected_products: string[];
  estimated_effort_hours: number;
  priority_score: number;
  identified_date: string;
  last_updated: string;
  tags: string[];
}

export interface TechnicalDebtSolution {
  solution_id: string;
  debt_code: string;
  title: string;
  description: string;
  implementation_steps: string[];
  estimated_effort_hours: number;
  required_skills: string[];
  risks: string[];
  benefits: string[];
  proposed_by: string;
  proposed_date: string;
  status: 'PROPOSED' | 'APPROVED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED';
}

// --- Types for Technical Debt Solutions API ---
export interface TechnicalDebtSolutionItem {
  id: string;
  solution_id: string;
  technical_debt_id: string;
  product_code: string;
  debt_item_id: string;
  project_key: string;
  /** Debt category from linked technical_debt row (nullable). */
  category?: string | null;
  solution_title: string;
  solution_description: string;
  solution_type: 'MANUAL' | 'AUTOMATED' | 'HYBRID';
  implementation_effort: 'LOW' | 'MEDIUM' | 'HIGH';
  estimated_impact: 'LOW' | 'MEDIUM' | 'HIGH';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'ON_HOLD';
  source: 'RECOMMENDATION' | 'MANUAL' | 'AUTOMATED';
  author: string | null;
  implementation_steps: string[];
  resources_required: string | null;
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TechnicalDebtSolutionsResponse {
  solutions: TechnicalDebtSolutionItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface TechnicalDebtSolutionDetailsResponse {
  id: string;
  solution_id: string;
  technical_debt_id: string;
  product_code: string;
  debt_item_id: string;
  project_key: string;
  /** Debt category from linked technical_debt row (nullable). */
  category?: string | null;
  solution_title: string;
  solution_description: string;
  solution_type: 'MANUAL' | 'AUTOMATED' | 'HYBRID';
  implementation_effort: 'LOW' | 'MEDIUM' | 'HIGH';
  estimated_impact: 'LOW' | 'MEDIUM' | 'HIGH';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'PENDING' | 'APPROVED' | 'IN_PROGRESS' | 'COMPLETED' | 'REJECTED' | 'ON_HOLD';
  source: 'RECOMMENDATION' | 'MANUAL' | 'AUTOMATED';
  author: string | null;
  implementation_steps: string[];
  resources_required: string | null;
  created_by: string;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UpdateTechnicalDebtSolutionPayload {
  solution_title?: string;
  solution_description?: string;
  solution_type?: string;
  implementation_effort?: string;
  estimated_impact?: string;
  priority?: string;
  status?: string;
  implementation_steps?: string[];
  resources_required?: any;
  approved_by?: string;
}

export interface UpdateTechnicalDebtSolutionResponse {
  message: string;
  solution_id: string;
  changes: Array<{
    field: string;
    old_value: any;
    new_value: any;
  }>;
  updated_solution: TechnicalDebtSolutionDetailsResponse;
}

export interface TechnicalDebtTrendDataPoint {
  date: string;
  total_debt_items: number;
  resolved_items: number;
  new_items: number;
  total_effort_hours: number;
  by_severity: TechnicalDebtSeverity;
}

export interface TechnicalDebtRecommendation {
  recommendation_id: string;
  title: string;
  description: string;
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  impact: 'HIGH' | 'MEDIUM' | 'LOW';
  effort: 'HIGH' | 'MEDIUM' | 'LOW';
  target_debt_items: string[];
  suggested_timeline: string;
  expected_benefits: string[];
  created_date: string;
}

export interface TechnicalDebtSummaryResponse {
  overall_summary: {
    total_debt_items: number;
    total_effort_hours: number;
    categories: {
      [categoryCode: string]: TechnicalDebtCategory;
    };
    severity_breakdown: TechnicalDebtSeverity;
    status_breakdown: {
      OPEN: number;
      IN_PROGRESS: number;
      RESOLVED: number;
      DEFERRED: number;
    };
  };
  top_priority_items: TechnicalDebtItem[];
  last_updated: string;
}

// Trend Summary Response (separate from regular summary)
export interface TechnicalDebtTrendSummaryResponse {
  summaries: TechnicalDebtSummaryItem[];
  count: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
  trends?: TechnicalDebtTrends;
}

export interface TechnicalDebtSummaryItem {
  id: string;
  total_debt_items: number;
  total_projects: number;
  average_risk_score: number | null;
  average_age: number | null;
  oldest_debt: number | null;
  newest_debt: number | null;
  collection_time_ms: number | null;
  average_debt_ratio: number | null;
  projects_meeting_coverage_gate: number | null;
  projects_with_critical_issues: number | null;
  avg_cognitive_complexity: number | null;
  projects_exceeding_duplication_limit: number | null;
  overall_availability: number | null;
  avg_error_rate: number | null;
  avg_p95_latency: number | null;
  avg_success_rate: number | null;
  total_sev1_incidents: number | null;
  total_sev2_incidents: number | null;
  by_category: { [key: string]: number };
  by_severity: { [key: string]: number };
  by_source: { [key: string]: number };
  report_timestamp: string;
  created_at: string;
}

export interface TechnicalDebtTrends {
  total_debt_items?: TrendMetric;
  total_projects?: TrendMetric;
  average_risk_score?: TrendMetric;
  average_age?: TrendMetric;
  projects_with_critical_issues?: TrendMetric;
  avg_cognitive_complexity?: TrendMetric;
  overall_availability?: TrendMetric;
  avg_error_rate?: TrendMetric;
  avg_p95_latency?: TrendMetric;
  total_sev1_incidents?: TrendMetric;
  total_sev2_incidents?: TrendMetric;
  severity_distribution?: {
    CRITICAL?: TrendMetric;
    HIGH?: TrendMetric;
    MEDIUM?: TrendMetric;
    LOW?: TrendMetric;
  };
}

export interface TrendMetric {
  current: number;
  previous: number;
  change: number;
  change_percent: number;
  trend: 'increasing' | 'decreasing' | 'stable';
}

export interface TechnicalDebtDetailsResponse {
  debt_item: TechnicalDebtItem;
  solutions: TechnicalDebtSolution[];
  related_items: TechnicalDebtItem[];
  affected_products_details: Array<{
    product_code: string;
    department: string;
    group: string;
    company: string;
  }>;
}

export interface TechnicalDebtTrendResponse {
  time_period: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY';
  data_points: TechnicalDebtTrendDataPoint[];
  predictions: {
    next_period_estimate: number;
    resolution_velocity: number;
  };
}

export interface TechnicalDebtRecommendationsResponse {
  recommendations: TechnicalDebtRecommendation[];
  prioritization_criteria: {
    high_impact_low_effort: TechnicalDebtRecommendation[];
    critical_severity: TechnicalDebtRecommendation[];
    most_affected_products: TechnicalDebtRecommendation[];
  };
}

export interface ProductTechnicalDebt {
  product_code: string;
  department: string;
  group: string;
  company: string;
  debt_items: TechnicalDebtItem[];
  total_effort_hours: number;
  severity_breakdown: TechnicalDebtSeverity;
  last_updated: string;
}

// --- Types for Technical Debt Project Details ---
export interface TechnicalDebtProjectInfo {
  project_key: string;
  project_name: string;
  product_code: string;
  company: string;
  group: string;
  department: string;
}

export interface TechnicalDebtCodeQuality {
  avg_debt_ratio_percent: number;
  avg_coverage_new_code_branch: number;
  avg_coverage_new_code_line: number;
  avg_overall_coverage_branch: number;
  avg_overall_coverage_line: number;
  avg_cognitive_complexity: number;
  avg_duplications_percent: number;
  total_bugs: number;
  total_code_smells: number;
  total_vulnerabilities: number;
}

export interface TechnicalDebtReliability {
  avg_mtbf_hours: number;
  avg_mttr_hours: number;
  avg_availability_percent: number;
  total_sev1_incidents: number;
  total_sev2_incidents: number;
}

export interface TechnicalDebtPerformance {
  avg_error_rate_percent: number;
  avg_p95_latency_ms: number;
  avg_success_rate_percent: number;
  avg_throughput_rps: number;
}

export interface TechnicalDebtProjectMetrics {
  total_debt_items: number;
  avg_age_days: number;
  avg_risk_score: number;
  severity_distribution: TechnicalDebtSeverity;
  category_distribution: { [category: string]: number };
  source_distribution: { [source: string]: number };
  code_quality: TechnicalDebtCodeQuality;
  reliability: TechnicalDebtReliability;
  performance: TechnicalDebtPerformance;
}

export interface TechnicalDebtItemDetail {
  debt_item_id: string;
  title: string;
  description: string;
  category: string;
  /** Present when a row exists in `technical_debt_solutions` for this item (dfp-api omits when absent). */
  solution_id?: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  source: string;
  status: string;
  risk_score: number;
  age_days: number;
  kloc: number;
  tech_debt_minutes: number;
  debt_ratio_percent: number;
  coverage_new_code_branch: number;
  coverage_new_code_line: number;
  overall_coverage_branch: number;
  overall_coverage_line: number;
  cognitive_complexity: number;
  duplications_percent: number;
  bugs_count: number;
  code_smells_count: number;
  vulnerabilities_count: number;
  mtbf_hours: number;
  mttr_hours: number;
  availability_percent: number;
  error_rate_percent: number;
  p95_latency_ms: number;
  success_rate_percent: number;
  throughput_rps: number;
  sev1_incidents: number;
  sev2_incidents: number;
  /** JSON string from API (Mint/infra payloads); may be absent or null. */
  metrics?: string | null;
  tags: string;
  standardized_tags: string;
  report_timestamp: string;
  created_at: string;
  updated_at: string;
}

export interface TechnicalDebtProjectDetailsResponse {
  project_info: TechnicalDebtProjectInfo;
  metrics: TechnicalDebtProjectMetrics;
  debt_items: TechnicalDebtItemDetail[];
}

// --- Types for Creating Technical Debt Item ---
export interface CreateTechnicalDebtItemPayload {
  project_key: string;
  project_name: string;
  debt_item_id: string;
  title: string;
  category: string;
  severity: string;
  product_code?: string;
  description?: string;
  source?: string;
  status?: string;
  risk_score?: number;
  age_days?: number;
  kloc?: number;
  tech_debt_minutes?: number;
  debt_ratio_percent?: number;
  coverage_new_code_branch?: number;
  coverage_new_code_line?: number;
  overall_coverage_branch?: number;
  overall_coverage_line?: number;
  cognitive_complexity?: number;
  duplications_percent?: number;
  bugs_count?: number;
  code_smells_count?: number;
  vulnerabilities_count?: number;
  mtbf_hours?: number;
  mttr_hours?: number;
  availability_percent?: number;
  error_rate_percent?: number;
  p95_latency_ms?: number;
  success_rate_percent?: number;
  throughput_rps?: number;
  sev1_incidents?: number;
  sev2_incidents?: number;
  metrics?: string;
  tags?: string;
  standardized_tags?: string;
}

// --- Types for Tech Stack ---

export interface TechStackBackendRepoParams {
  product_code?: string;
  language?: string;
  go_version?: string;
  kotlin_version?: string;
  java_version?: string;
  framework?: string;
  database_type?: string;
  has_codeowners?: boolean;
  renovate_enabled?: boolean;
  using_mf_common?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface TechStackFrontendRepoParams {
  product_code?: string;
  language?: string;
  typescript_version?: string;
  nodejs_version?: string;
  package_manager?: string;
  frontend_framework?: string;
  // css_tech omitted in v1 — list endpoint does not return this field
  has_mfui_components?: boolean;
  search?: string;
  limit?: number;
  offset?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export interface TechStackFrameworkItem {
  type: string;
  name: string;
  version: string;
}

export interface TechStackTypeVersionItem {
  type: string;
  version: string;
}

export interface TechStackBackendRepoSummary {
  repository: string;
  repository_url: string;
  product_code?: string;
  language: string;
  go_version?: string;
  kotlin_version?: string;
  java_version?: string;
  frameworks: TechStackFrameworkItem[];
  database_type?: TechStackTypeVersionItem;
  database_library?: TechStackTypeVersionItem;
  database_connector?: TechStackTypeVersionItem;
  database_migration_info?: TechStackTypeVersionItem;
  communication_protocol?: Array<{ type: string; library: string }>;
  logging_libraries?: string[];
  messaging_libraries?: string[];
  cache_libraries?: string[];
  test_framework?: string[];
  has_codeowners: boolean;
  renovate_enabled: boolean;
  using_mf_common: boolean;
  sbom?: { total_packages: number };
  pushed_at: string;
}

export interface TechStackBackendListResponse {
  repositories: TechStackBackendRepoSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface TechStackDebtEolVersion {
  name: string;
  version: string;
  status: string;
  end_of_support?: string;
}

export interface TechStackDebtLibrary {
  library: string;
  outdated_import: string;
  latest_import: string;
  status: string;
}

export interface TechStackBackendRepoDetail extends TechStackBackendRepoSummary {
  database_library?: TechStackTypeVersionItem;
  database_connector?: TechStackTypeVersionItem;
  communication_protocol?: Array<{ type: string; library: string }>;
  logging_libraries?: string[];
  messaging_libraries?: string[];
  build_tool?: string;
  go_modules?: boolean;
  dependabot_enabled?: boolean;
  use_logging_standard?: boolean;
  technical_debt_indicator?: {
    eol_versions?: TechStackDebtEolVersion[];
    libraries?: TechStackDebtLibrary[];
  };
  sbom?: {
    total_packages: number;
    direct_dependencies: number;
    license_summary: Record<string, number>;
  };
}

export interface TechStackFrontendRepoSummary {
  repository: string;
  repository_url: string;
  product_code?: string;
  language: string;
  typescript_version?: string;
  nodejs_version?: string;
  frontend_framework?: string;
  package_manager?: string;
  build_tool?: string;
  test_framework?: string;
  has_mfui_components?: boolean;
  css_tech?: string;
  state_management?: string;
  formatter?: string;
  js_linter?: string;
  ui_catalog?: string;
  frontend_library?: string;
  sbom?: { total_packages: number };
  pushed_at: string;
}

export interface TechStackFrontendListResponse {
  repositories: TechStackFrontendRepoSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface TechStackFrontendRepoDetail extends TechStackFrontendRepoSummary {
  css_tech?: string;
  state_management?: string;
  ui_catalog?: string;
  formatter?: string;
  js_linter?: string;
  ui_library?: string;
  e2e_test_tool?: string;
  technical_debt_indicator?: {
    eol_versions?: TechStackDebtEolVersion[];
    libraries?: TechStackDebtLibrary[];
  };
  sbom?: {
    total_packages: number;
    direct_dependencies: number;
    license_summary: Record<string, number>;
  };
}

export interface TechStackAnalyticsGroup {
  key: string;
  count: number;
  percentage: number;
}

export interface TechStackAnalyticsResponse {
  group_by: string;
  groups: TechStackAnalyticsGroup[];
  total: number;
}

export interface TechStackSecuritySummaryResponse {
  renovate_enabled_percent: number;
  codeowners_percent: number;
  mf_common_percent: number;
  eol_count: number;
  license_violations: number;
}

export interface TechStackTechnicalDebtRepo {
  repository: string;
  language: string;
  product_code?: string;
  technical_debt_indicator: {
    eol_versions: TechStackDebtEolVersion[];
    libraries?: TechStackDebtLibrary[];
  };
}

export interface TechStackTechnicalDebtResponse {
  repositories: TechStackTechnicalDebtRepo[];
  total: number;
}

export interface TechStackFrameworkSummary {
  name: string;
  language: string;
  repo_count: number;
  repositories: string[];
}

export interface TechStackFrameworksResponse {
  frameworks: TechStackFrameworkSummary[];
}

export interface TechStackDatabaseSummary {
  type: string;
  repo_count: number;
  repositories: string[];
}

export interface TechStackDatabasesResponse {
  databases: TechStackDatabaseSummary[];
}

/** One POST aggregates many products (same payload shape as GET /product-master/:code per item). */
export interface ProductMasterBulkExportItem {
  productCode: string;
  productMasterDetails?: Record<string, unknown>;
  error?: string;
  message?: string;
}

export interface ProductMasterBulkExportResponse {
  exportedAt: string;
  rowCount: number;
  items: ProductMasterBulkExportItem[];
}

/** Same shape as Product Master UI “Export as JSON” download (`POST /product-master/export` default). */
export interface ProductMasterJsonFileExportResponse {
  exported_at: string;
  product_count: number;
  products: Record<string, unknown>[];
}

export * from './productMaster';

/** GET /v1/tech_stack/eol/{product_code} — distinct EOL runtime tokens from tech stack inventory. */
export interface TechStackProductEolResponse {
  product_code: string;
  eol_data: string[];
}

// --- Types for Architecture Healthcheck ---
export type ArchHealthSeverity = 'ARCH_SEV1' | 'ARCH_SEV2' | 'ARCH_SEV3' | 'ARCH_SEV4';

export interface ArchHealthCategoryScore {
  category: string;
  score: number;
  severity: ArchHealthSeverity;
}

export type ArchHealthFeedbackState = 'PENDING' | 'ACKNOWLEDGED' | 'RESOLVED';

export interface ArchHealthFindingFeedback {
  author: string;
  comment: string;
  created_at?: string;
}

export interface ArchHealthFinding {
  product_code: string;
  finding_id: string;
  category: string;
  question: string;
  score: number;
  severity: ArchHealthSeverity;
  evidence: string | null;
  remarks: string | null;
  feedbacks: ArchHealthFindingFeedback[] | null;
  feedback_state: ArchHealthFeedbackState | null;
  scan_date: string;
}

export interface ArchHealthRecommendation {
  id: string;
  severity: ArchHealthSeverity;
  text: string;
}

export type ArchHealthReportType = 'PRODUCT' | 'QUANTUM';

export interface ArchHealthProductSummary {
  id: string;
  product_code: string;
  product_name?: string;
  type?: ArchHealthReportType | null;
  overall_score: number;
  overall_severity: ArchHealthSeverity;
  scan_date: string;
  weakest_category: string | null;
  weakest_score: number | null;
  repositories: string[] | null;
}

export interface ArchHealthProductsListResponse {
  products: ArchHealthProductSummary[];
  total: number;
  limit: number;
  offset: number;
  filters_applied: Record<string, any>;
}

export interface ArchHealthImpactAnalysisItem {
  finding_id: string;
  finding: string;
  technical_impact: string;
  short_term_risk: string;
  long_term_risk: string;
  real_world_example: string;
}

export interface ArchHealthProductDetail {
  id: string;
  product_code: string;
  type?: ArchHealthReportType | null;
  overall_score: number;
  overall_severity: ArchHealthSeverity;
  scan_date: string;
  repositories: string[] | null;
  category_scores: ArchHealthCategoryScore[];
  findings: ArchHealthFinding[];
  recommendations: ArchHealthRecommendation[];
  impact_analysis?: ArchHealthImpactAnalysisItem[] | null;
}

export interface ArchHealthHistoryPoint {
  scan_date: string;
  created_at: string;
  overall_score: number;
  overall_severity: string;
  weakest_category: string | null;
  weakest_score: number | null;
  category_scores: ArchHealthCategoryScore[] | null;
  recommendation_count: number | null;
}

export interface ArchHealthProductHistoryResponse {
  product_code: string;
  history: ArchHealthHistoryPoint[];
}

export interface ArchHealthSummaryResponse {
  total_products_assessed: number;
  last_scan_date: string | null;
  average_overall_score: number;
  overall_severity_distribution: {
    ARCH_SEV1: number;
    ARCH_SEV2: number;
    ARCH_SEV3: number;
    ARCH_SEV4: number;
  };
  category_ranking: Array<{
    rank: number;
    category: string;
    average_score: number;
    average_severity: string;
  }>;
  top_critical_products: ArchHealthProductSummary[];
  most_improved: Array<{
    product_code: string;
    current_score: number;
    previous_score: number;
    delta: number;
  }>;
}

export interface ArchHealthFindingsListResponse {
  findings: ArchHealthFinding[];
  total: number;
  limit: number;
  offset: number;
  filters_applied: Record<string, any>;
}

// --- Types for Security Scan Issues ---
export type SecurityScanIssueSeverity = 'BLOCKER' | 'CRITICAL' | 'MAJOR' | 'MINOR' | 'INFO';
export type SecurityScanIssueSlaTier = 'critical' | 'high' | 'medium' | 'none';
export type SecurityScanIssueLifecycleStatus =
  | 'detected'
  | 'in_progress'
  | 'testing'
  | 'resolved'
  | 'deferred'
  | 'unscanned';
export type SecurityScanIssueSlaState = 'overdue' | 'due_soon' | 'on_track' | 'n/a';
/**
 * Precise FINDINGS.md row status. lifecycle_status collapses these into
 * top-level buckets — this is the sub-status used for the UI badge.
 */
export type SecurityScanIssueFindingsMdStatus =
  | 'open'
  | 'fixed'
  | 'accepted-risk'
  | 'false-positive'
  | 'unscanned';
/** Manager review workflow state for deferred findings. */
export type SecurityScanIssueReviewerState = 'unreviewed' | 'action-needed';

export interface SecurityScanIssueItem {
  id: string;
  external_id: string;
  sonar_project_key: string;
  sonar_issue_key: string;
  repository: string | null;
  product_code: string | null;
  finding_id: string;
  title: string;
  description: string;
  rule_key: string;
  severity: SecurityScanIssueSeverity;
  sla_tier: SecurityScanIssueSlaTier;
  status_sonar: string;
  file_path: string;
  line: number | null;
  detected_date: string;
  sonar_url: string | null;
  lifecycle_status: SecurityScanIssueLifecycleStatus;
  lifecycle_status_changed_at: string;
  lifecycle_status_changed_by: string | null;
  lifecycle_note: string | null;
  sla_due_date: string;
  sla_state: SecurityScanIssueSlaState;
  days_until_due: number;
  // FINDINGS.md-flow fields (nullable so legacy rows still type-check).
  findings_md_status?: SecurityScanIssueFindingsMdStatus | null;
  findings_md_path?: string | null;
  findings_md_url?: string | null;
  first_seen?: string | null;
  last_seen?: string | null;
  division?: string | null;
  reviewer_state?: SecurityScanIssueReviewerState | null;
  action_note?: string | null;
}

export interface SecurityScanIssueStatusHistoryEntry {
  /**
   * Not limited to SecurityScanIssueLifecycleStatus: the API records manager
   * review transitions (`unreviewed` -> `action-needed`) and a `(scan)` marker
   * for rescan refreshes in the same history table, and types both columns as
   * a plain string. Kept open so new markers do not break the client.
   */
  from_status: string | null;
  to_status: string;
  changed_by: string | null;
  changed_at: string;
  note: string | null;
}

export interface SecurityScanIssueDetail extends SecurityScanIssueItem {
  status_history: SecurityScanIssueStatusHistoryEntry[];
}

export interface SecurityScanIssueListResponse {
  items: SecurityScanIssueItem[];
  page: number;
  page_size: number;
  total: number;
}

export interface SecurityScanIssueListParams {
  /**
   * Single value, array, or comma-separated string — API does the IN-clause.
   * Typed as `string | string[]` rather than the narrower enum unions
   * because a comma-separated string already widens to `string`, which
   * would otherwise make the enum member of the union redundant.
   */
  severity?: string | string[];
  sla_tier?: string | string[];
  lifecycle_status?: string | string[];
  findings_md_status?: string | string[];
  reviewer_state?: string | string[];
  division?: string;
  /** Single value or array (or comma-separated string) — API does the IN-clause. */
  product_code?: string | string[];
  repository?: string | string[];
  sla_state?: string | string[];
  q?: string;
  page?: number;
  page_size?: number;
}

export interface SecurityScanIssueSummaryBaseline {
  blocker: number;
  critical: number;
  deferred: number;
  total: number;
}

export interface SecurityScanIssueSummaryRemaining {
  blocker: number;
  critical: number;
  deferred_unreviewed: number;
  deferred_action_needed: number;
  total: number;
}

export interface SecurityScanIssueSummaryRow {
  division: string;
  division_head: string;
  repos_count: number;
  unscanned_repos_count: number;
  baseline: SecurityScanIssueSummaryBaseline;
  remaining: SecurityScanIssueSummaryRemaining;
  progress_percent: number;
}

export interface SecurityScanIssueSummary {
  snapshot_date: string;
  divisions: SecurityScanIssueSummaryRow[];
  total: {
    baseline_total: number;
    remaining_total: number;
    progress_percent: number;
  };
}

export interface SecurityScanIssueDivision {
  division: string;
  division_head: string;
}

export interface SecurityScanIssueStatusUpdateRequest {
  status: SecurityScanIssueLifecycleStatus;
  note?: string;
}

/**
 * Request body for the manager-only review endpoint
 * (POST /security-scan-issues/{id}/review). The action_note is the
 * manager's free-text explanation of what needs to happen with this
 * deferred finding — required and persisted to lifecycle_note.
 */
export interface SecurityScanIssueReviewRequest {
  action_note: string;
}

export interface SecurityScanIssueAnalyticsBucket {
  key: string;
  count: number;
}

export interface SecurityScanIssueAnalytics {
  scope: string;
  total: number;
  by_severity: SecurityScanIssueAnalyticsBucket[];
  by_sla_state: SecurityScanIssueAnalyticsBucket[];
  by_lifecycle_status: SecurityScanIssueAnalyticsBucket[];
  by_division: SecurityScanIssueAnalyticsBucket[];
}

export interface SecurityScanIssueFacets {
  products: string[];
  repositories: string[];
}

export type SecurityScanIssueTrendGranularity = 'week' | 'month';

export interface SecurityScanIssueTrendBucket {
  /** ISO label — e.g. "2026-W19" for week or "2026-05" for month. */
  period: string;
  /** First day of the bucket (YYYY-MM-DD). */
  start_date: string;
  open: number;
  fixed: number;
  deferred: number;
  unscanned: number;
  total: number;
}

export interface SecurityScanIssueTrend {
  granularity: SecurityScanIssueTrendGranularity;
  buckets: SecurityScanIssueTrendBucket[];
}

/**
 * Response from POST /security-scan-issues/sync.
 *
 * `status`:
 *   - `accepted` — a K8s job was created; `jobName` is set. The button
 *     should enter cooldown.
 *   - `conflict` — a previous sync is still running on the ETL side. The
 *     button should also enter cooldown (no point hammering).
 *   - `error`    — server-side failure; surface the message.
 */
export interface SecurityScanIssueSyncResponse {
  status: 'accepted' | 'conflict' | 'error';
  message: string;
  jobName?: string;
  source: string;
  traceId?: string;
  timestamp: string;
}

export interface ArchHealthSyncResponse {
  status: 'success' | 'conflict' | 'error';
  message: string;
  jobName?: string;
  traceId: string;
  timestamp: string;
}

// --- API Interface ---
export interface DfpApi {
  getHealth(): Promise<{ status: string }>;
  getProductList(): Promise<ProductListResponse>;
  getProductDetails(productCode: string): Promise<ProductDetails>;
  // Product Master APIs
  getProductMasterDetails(productCode: string): Promise<any>;
  getProductMasterMaturity(productCode: string): Promise<{ maturityLevel: any }>;
  searchProductMaster(filters: any): Promise<any>;
  exportProductMasterBulk(productCodes: string[]): Promise<ProductMasterBulkExportResponse>;
  getProductMasterValueCatalog(): Promise<any>;
  getProductMasterHierarchyOptions(): Promise<import('./productMaster/productMasterHierarchyTypes').ProductMasterHierarchyOptionsResponse>;
  // Product Master change request (GitHub Actions workflow → PR)
  dispatchProductEditRequest(payload: {
    productCode: string;
    changes: any;
    reason: string;
    requestedBy: string;
    requestType?: 'edit' | 'create';
    baseBranch?: string;
    dryRun?: boolean;
  }): Promise<{
    workflowId: string;
    ref: string;
    dispatchedAt: string;
    status: 'queued';
    mode: 'workflow_dispatch';
    note?: string;
  }>;
  getProductEditRequestStatus(prNumber: number): Promise<any>;
  listProductEditRequests(filters?: {
    status?: string;
    productCode?: string;
    includeReviewStatus?: boolean;
  }): Promise<any>;
  getMintSummary(): Promise<MintSummaryResponse>;
  getMintProductResults(productCode: string): Promise<MintProductResults>;
  refreshProductData(productCode: string, sources?: string[]): Promise<any>;
  getConfig(): Promise<{ lumos: { baseUrl: string; publicUrl?: string } }>;
  checkLumosAccessibility(productCode: string): Promise<boolean>;
  getStandards(): Promise<StandardComplianceResponse>;
  getStandardsAdoption(): Promise<StandardsAdoptionResponse>;
  getProductsCompliance(): Promise<ProductsComplianceResponse>;
  getStandardDetails(standardCode: string): Promise<StandardDetails>;
  updateStandardAdoptionStatus(productCode: string, standardAdoptionStatus: Record<string, string>): Promise<{ status: string; message: string }>;
  getProductStandardDetails(productCode: string): Promise<ProductStandardDetails>;
  getStandardComplianceSummary(startDate?: string, endDate?: string): Promise<StandardComplianceSummaryListResponse>;
  // Common Platform Integration APIs
  getCommonPlatformIntegration(): Promise<CommonPlatformOverallSummary>;
  getPlatformIntegrationDetails(platformCode: string): Promise<PlatformIntegrationSummary>;
  getProductPlatformIntegration(productCode: string): Promise<ProductPlatformIntegration>;
  updateProductPlatformIntegration(platformCode: string, platformIntegrationStatus: Record<string, string>): Promise<{ status: string; message: string }>;
  // Platform Management APIs
  getAllPlatforms(): Promise<any>;
  getPlatformByCode(platformCode: string): Promise<any>;
  createOrUpdatePlatform(platformCode: string, payload: any): Promise<any>;
  // Standard Management APIs
  getAllStandards(): Promise<any>;
  getStandardByCode(standardCode: string): Promise<any>;
  createOrUpdateStandard(standardCode: string, payload: any): Promise<any>;
  getStandardsBackground(): Promise<{ content: string }>;
  // Technical Debt APIs
  getTechnicalDebtDashboard(): Promise<TechnicalDebtDashboardResponse>;
  getTechnicalDebtSummary(): Promise<TechnicalDebtSummaryResponse>;
  getTechnicalDebtTrendSummary(startDate?: string, endDate?: string): Promise<TechnicalDebtTrendSummaryResponse>;
  getTechnicalDebtDetails(debtCode: string): Promise<TechnicalDebtDetailsResponse>;
  getTechnicalDebtTrends(timePeriod?: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY'): Promise<TechnicalDebtTrendResponse>;
  getTechnicalDebtRecommendations(): Promise<TechnicalDebtRecommendationsResponse>;
  getProductTechnicalDebt(productCode: string): Promise<ProductTechnicalDebt>;
  getTechnicalDebtProjectDetails(projectKey: string): Promise<TechnicalDebtProjectDetailsResponse>;
  updateTechnicalDebtItem(debtCode: string, payload: Partial<TechnicalDebtItem> | CreateTechnicalDebtItemPayload): Promise<{ status: string; message: string }>;
  createTechnicalDebtSolution(debtCode: string, solution: Omit<TechnicalDebtSolution, 'solution_id'>): Promise<{ status: string; solution_id: string }>;
  createTechnicalDebtItem(payload: CreateTechnicalDebtItemPayload): Promise<{ status: string; debt_item_id: string }>;
  getTechnicalDebtSolutions(params?: {
    solution_id?: string;
    debt_item_id?: string;
    project_key?: string;
    status?: string;
    priority?: string;
    source?: string;
   /** Filter solutions by linked debt item category (nullable on server). */ 
    category?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<TechnicalDebtSolutionsResponse>;
  getTechnicalDebtSolutionDetails(solutionId: string): Promise<TechnicalDebtSolutionDetailsResponse>;
  updateTechnicalDebtSolution(solutionId: string, payload: UpdateTechnicalDebtSolutionPayload): Promise<UpdateTechnicalDebtSolutionResponse>;
  getTechnicalDebtItems(params?: {
    category?: string;
    severity?: string;
    status?: string;
    source?: string;
    project_key?: string;
    limit?: number;
    offset?: number;
  }): Promise<any>;
  createNewTechnicalDebtSolution(payload: any): Promise<{ id: string; solution_id: string; debt_item_id: string }>;
  updateTechnicalDebtSolution(solutionId: string, payload: any): Promise<any>;
  // Tech Stack APIs
  getTechStackAnalytics(params?: { group_by?: string; repository_type?: string }): Promise<TechStackAnalyticsResponse>;
  getTechStackSecuritySummary(): Promise<TechStackSecuritySummaryResponse>;
  getTechStackBackendRepos(params?: TechStackBackendRepoParams): Promise<TechStackBackendListResponse>;
  getTechStackBackendRepoDetail(repoName: string): Promise<TechStackBackendRepoDetail>;
  getTechStackFrontendRepos(params?: TechStackFrontendRepoParams): Promise<TechStackFrontendListResponse>;
  getTechStackFrontendRepoDetail(repoName: string): Promise<TechStackFrontendRepoDetail>;
  getTechStackFrameworks(): Promise<TechStackFrameworksResponse>;
  getTechStackDatabases(): Promise<TechStackDatabasesResponse>;
  updateTechStackRepoProductCode(repoName: string, productCode: string, repoType: 'backend' | 'frontend'): Promise<{ status: string; message: string }>;
  getTechStackTechnicalDebt(params: { debt_type: string; language?: string; product_code?: string; severity?: string }): Promise<TechStackTechnicalDebtResponse>;
  getTechStackProductEol(productCode: string): Promise<TechStackProductEolResponse>;
  // Architecture Healthcheck APIs
  getArchHealthProducts(params?: {
    severity?: ArchHealthSeverity;
    type?: ArchHealthReportType;
    min_score?: number;
    max_score?: number;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<ArchHealthProductsListResponse>;
  getArchHealthProductDetail(productCode: string): Promise<ArchHealthProductDetail>;
  getArchHealthAssessmentDetail(assessmentId: string): Promise<ArchHealthProductDetail>;
  updateArchHealthAssessment(assessmentId: string, payload: { product_code: string }): Promise<{ id: string; product_code: string; previous_product_code: string }>;
  getArchHealthProductHistory(productCode: string, params?: { from_date?: string; to_date?: string }): Promise<ArchHealthProductHistoryResponse>;
  getArchHealthSummary(): Promise<ArchHealthSummaryResponse>;
  getArchHealthFindings(params?: {
    product_code?: string;
    category?: string;
    severity?: ArchHealthSeverity;
    limit?: number;
    offset?: number;
  }): Promise<ArchHealthFindingsListResponse>;
  submitArchHealthFindingFeedback(payload: ArchHealthFindingFeedbackPayload): Promise<void>;
  putArchHealthFindingFeedbacks(productCode: string, findingId: string, feedbacks: ArchHealthFindingFeedbackEntry[]): Promise<void>;
  resolveArchHealthFindingFeedback(productCode: string, findingId: string): Promise<void>;
  // Security Scan Issues APIs
  getSecurityScanIssueSummary(): Promise<SecurityScanIssueSummary>;
  getSecurityScanIssues(params?: SecurityScanIssueListParams): Promise<SecurityScanIssueListResponse>;
  getSecurityScanIssueDetail(id: string): Promise<SecurityScanIssueDetail>;
  getSecurityScanIssueDivisions(): Promise<SecurityScanIssueDivision[]>;
  updateSecurityScanIssueStatus(
    id: string,
    body: SecurityScanIssueStatusUpdateRequest,
  ): Promise<SecurityScanIssueItem>;
  getSecurityScanIssueAnalytics(): Promise<SecurityScanIssueAnalytics>;
  getSecurityScanIssueTrend(
    granularity?: SecurityScanIssueTrendGranularity,
  ): Promise<SecurityScanIssueTrend>;
  getSecurityScanIssueFacets(): Promise<SecurityScanIssueFacets>;
  triggerSecurityScanIssueSync(): Promise<SecurityScanIssueSyncResponse>;
  /**
   * Manager-only. The client sends the user's group ownership refs in
   * the X-Forwarded-Groups header; upstream rejects with 403 if the
   * caller is not in "MFBC CTO Office" or "MFBC CISO Office". Resolves with the updated row;
   * throws with the upstream error message on 4xx.
   */
  submitSecurityScanIssueReview(
    id: string,
    body: SecurityScanIssueReviewRequest,
  ): Promise<SecurityScanIssueItem>;
  triggerArchHealthSync(): Promise<ArchHealthSyncResponse>;
}

export interface ArchHealthFindingFeedbackEntry {
  author: string;
  comment: string;
}

export interface ArchHealthFindingFeedbackPayload {
  assessment_id: string;
  feedbacks: Array<{
    finding_id: string;
    entries: ArchHealthFindingFeedbackEntry[];
  }>;
}

// --- ApiRef ---
export const dfpApiRef = createApiRef<DfpApi>({
  id: 'plugin.dfp.service',
});
